import { get, isNil } from 'lodash';
import { createSelector } from 'reselect';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { StandardCardProps } from 'src/components/StandardCardView/StandardCard/StandardCard';
import { IdentityPropsConfig } from 'src/components/StandardCardView/StandardCardView.types';
import {
  GenerateCardProps,
  generateCardProps,
  retrieveIdentityPropsConfig,
} from 'src/components/StandardCardView/StandardCardView.utils';
import { CardViewItem } from 'src/components/StandardCardView/UIData.types';
import { TenantConfigViewData, TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { viewItemToFinancialProps } from 'src/pages/Hindsighting/CategoryRecap/CategorySummary/CategorySummary.selectors';
import { HashType, getUniqueDataFromCache } from 'src/services/pivotServiceCache';
import { AppState } from 'src/store';
import { noImage } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';

export const selectViewDefnState = (state: AppState) => state.pages.assortmentBuild.styleCollection.viewDefnState;
export const selectCollectionDataState = (state: AppState) =>
  state.pages.assortmentBuild.styleCollection.collectionDataState;
export const selectSubheader = (state: AppState) => state.subheader;
export const selectItemsDataState = (state: AppState) => state.pages.assortmentBuild.styleCollection.itemsDataState;

const selectViewDefn = (state: AppState) => state.pages.assortmentBuild.styleCollection.viewDefn;
const selectStyleCollectionData = (state: AppState) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.styleCollection, HashType.styleCollection)?.tree ?? [];
};
const selectStyleCollectionItemsData = (state: AppState) => {
  return getUniqueDataFromCache(state.pages.assortmentBuild.styleCollection, HashType.styleCollectionItems)?.tree ?? [];
};

export const selectFormattedItemsData = createSelector(
  selectStyleCollectionItemsData,
  selectSubheader,
  selectViewDefn,
  (itemsData, subheader, viewDefn) => {
    const defns = {
      view: (viewDefn?.items as unknown as TenantConfigViewData) ?? ({} as TenantConfigViewData),
      rollup: {} as TenantConfigViewData,
    };

    const processedData = processDataForCardView({ styles: itemsData, subheader, defns });
    const children: CardViewItem[] = get(processedData, '[0].items', []);

    const processedCardProps: StandardCardProps[] = children.map((pd) => {
      const dataCardProps: GenerateCardProps = {
        styleItem: pd,
        identityPropsConfig: retrieveIdentityPropsConfig(
          viewDefn as unknown as TenantConfigViewData,
        ) as IdentityPropsConfig,
        adornments: [],
      };
      const cardProps: StandardCardProps = generateCardProps(dataCardProps);
      return cardProps;
    });

    return processedCardProps;
  },
);

export const selectFormattedCollectionData = createSelector(
  selectViewDefn,
  selectStyleCollectionData,
  (viewDefn, collectionData) => {
    const configs = viewDefn?.collection.view;

    let image: string = noImage;
    let description: string = '-';
    let name: string = '-';

    if (isNil(configs)) {
      return { image, description, name };
    }

    const [imageConfig, descriptionConfig, nameConfig] = configs;
    const collection: BasicPivotItem | undefined = collectionData[0];

    if (isNil(collection)) {
      return { image, description, name };
    }

    image = collection[imageConfig.dataIndex];
    description = collection[descriptionConfig.dataIndex];
    name = collection[nameConfig.dataIndex];

    return { image, description, name };
  },
);

// TODO: may need to point at macro data instead,
// for now assumes macro data is available from the collection dataApi response
export const selectFormattedMacroData = createSelector(
  selectViewDefn,
  selectStyleCollectionData,
  (viewDefn, collectionData) => {
    const collection: BasicPivotItem | undefined = collectionData[0];
    const keyFinancials: MacrosProps[] = (viewDefn?.macros.view ?? []).map((viewItem) =>
      viewItemToFinancialProps(viewItem as TenantConfigViewItem, collection),
    );

    return keyFinancials;
  },
);
